import { useEffect, useState } from 'react'
import { Loader2 } from 'lucide-react'
import { Button } from '@/shared/components/ui/button'
import { getApiErrorMessage } from '@/shared/api/getApiErrorMessage'
import { useClockIn, useClockOut, useOfficeClockStatus } from '@/entities/office-time'
import { readOfficeLocation } from './lib/readOfficeLocation'

const TICK_MS = 30_000
const MS_PER_MINUTE = 60_000
const MINUTES_PER_HOUR = 60
const ERROR_VISIBLE_MS = 10_000

function useElapsedMinutes(startedAt: string | null): number {
  const [now, setNow] = useState(() => Date.now())

  useEffect(() => {
    if (!startedAt) return
    setNow(Date.now())
    const timer = window.setInterval(() => setNow(Date.now()), TICK_MS)
    return () => window.clearInterval(timer)
  }, [startedAt])

  if (!startedAt) return 0
  return Math.max(0, Math.floor((now - new Date(startedAt).getTime()) / MS_PER_MINUTE))
}

function formatClockElapsed(totalMinutes: number): string {
  return `${Math.floor(totalMinutes / MINUTES_PER_HOUR)}h ${totalMinutes % MINUTES_PER_HOUR}m`
}

export function OfficeClockButton() {
  const status = useOfficeClockStatus()
  const clockIn = useClockIn()
  const clockOut = useClockOut()
  const [error, setError] = useState<string | null>(null)
  const [working, setWorking] = useState(false)

  const open = status.data?.open ?? null
  const elapsedMinutes = useElapsedMinutes(open?.startedAt ?? null)

  useEffect(() => {
    if (!error) return
    const timer = window.setTimeout(() => setError(null), ERROR_VISIBLE_MS)
    return () => window.clearTimeout(timer)
  }, [error])

  if (!status.data?.enabled || !status.data.eligible) return null

  const run = async (action: 'in' | 'out') => {
    setError(null)
    setWorking(true)
    try {
      const where = await readOfficeLocation()
      if (action === 'in') await clockIn.mutateAsync(where)
      else await clockOut.mutateAsync(where)
    } catch (e) {
      setError(await getApiErrorMessage(e))
      void status.refetch()
    } finally {
      setWorking(false)
    }
  }

  return (
    <div className="relative flex items-center gap-2">
      {open ? (
        <>
          <span className="inline-flex items-center gap-1.5 rounded-full border border-line bg-hover px-2.5 py-[5px] text-[12px] font-medium text-ink-2">
            On the clock · {formatClockElapsed(elapsedMinutes)}
          </span>
          <Button variant="outline" size="sm" disabled={working} onClick={() => void run('out')}>
            {working && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            Clock out
          </Button>
        </>
      ) : (
        <Button variant="outline" size="sm" disabled={working} onClick={() => void run('in')}>
          {working && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
          Clock in
        </Button>
      )}
      {error && (
        <div
          role="alert"
          className="absolute right-0 top-full z-40 mt-1.5 w-72 rounded-md border border-bad-bd bg-bad-bg px-3 py-2 text-xs text-bad shadow-md"
        >
          {error}
        </div>
      )}
    </div>
  )
}
